const moneyFormat = (value, currency) => {
  if (!Number.isFinite(value)) return '';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'GBP',
    }).format(value / 100);
  } catch (_error) {
    return `£${(value / 100).toFixed(2)}`;
  }
};

const DV_RECOMMENDATION_STATS_KEY = 'dvRecommendationStats';

const readRecommendationStats = () => {
  try {
    return JSON.parse(localStorage.getItem(DV_RECOMMENDATION_STATS_KEY) || '{}') || {};
  } catch (_error) {
    return {};
  }
};

const writeRecommendationStats = (stats) => {
  try {
    localStorage.setItem(DV_RECOMMENDATION_STATS_KEY, JSON.stringify(stats));
  } catch (_error) {
    // Recommendation ranking is best-effort when storage is unavailable.
  }
};

const recommendationProductId = (card) => card?.getAttribute('data-dv-product-id') || '';
const recommendationProductHandle = (card) => card?.getAttribute('data-dv-product-handle') || '';

const mergeRecommendationStats = (...sources) => sources.reduce((merged, source) => {
  if (!source || typeof source !== 'object') return merged;

  Object.entries(source).forEach(([productId, productStats]) => {
    if (!productStats || typeof productStats !== 'object') return;
    const currentStats = merged[productId] || { views: 0, clicks: 0 };
    merged[productId] = {
      views: Math.max(Number(currentStats.views) || 0, Number(productStats.views) || 0),
      clicks: Math.max(Number(currentStats.clicks) || 0, Number(productStats.clicks) || 0),
    };
  });

  return merged;
}, {});

const readSiteRecommendationStats = () => {
  if (window.dvRecommendationStats && typeof window.dvRecommendationStats === 'object') {
    return window.dvRecommendationStats;
  }
  return {};
};

const readMergedRecommendationStats = () => mergeRecommendationStats(readRecommendationStats(), readSiteRecommendationStats());

const recommendationScore = (stats, productId) => {
  const productStats = stats[productId] || {};
  return (Number(productStats.clicks) || 0) * 3 + (Number(productStats.views) || 0);
};

const sortRecommendationCards = (carousel, stats = readMergedRecommendationStats()) => {
  const track = carousel.querySelector('[data-dv-carousel-track]');
  if (!track) return;

  Array.from(track.querySelectorAll('[data-dv-recommendation-card]'))
    .map((card, index) => ({ card, index, productId: recommendationProductId(card) }))
    .sort((left, right) => {
      const scoreDiff = recommendationScore(stats, right.productId) - recommendationScore(stats, left.productId);
      return scoreDiff || left.index - right.index;
    })
    .forEach(({ card }) => track.appendChild(card));
};

const markRecommendationPreloaded = (card) => {
  card.setAttribute('data-dv-recommendation-preloaded', 'true');
  card.removeAttribute('data-dv-recommendation-preloading');
};

const preloadRecommendationImage = (card) => {
  const src = card?.getAttribute('data-dv-recommendation-preload-src');
  if (!src || card.hasAttribute('data-dv-recommendation-preloaded') || card.hasAttribute('data-dv-recommendation-preloading')) {
    return Promise.resolve();
  }

  card.setAttribute('data-dv-recommendation-preloading', 'true');

  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;

      if (typeof image.decode === 'function') {
        image.decode()
          .catch(() => {})
          .finally(() => {
            markRecommendationPreloaded(card);
            resolve();
          });
        return;
      }

      markRecommendationPreloaded(card);
      resolve();
    };

    image.decoding = 'async';
    image.loading = 'eager';
    image.onload = finish;
    image.onerror = () => {
      if (settled) return;
      settled = true;
      card.removeAttribute('data-dv-recommendation-preloading');
      resolve();
    };
    image.src = src;

    if (image.complete) {
      finish();
    }
  });
};

const preloadRecommendationImages = (carousel, startIndex = 0, batchSize = 8) => {
  const cards = Array.from(carousel.querySelectorAll('[data-dv-recommendation-card]'));
  const endIndex = Math.min(cards.length, startIndex + batchSize);

  cards.slice(startIndex, endIndex).forEach((card) => {
    preloadRecommendationImage(card);
  });
};

const scheduleRecommendationIdleWork = (callback) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 900 });
    return;
  }

  window.setTimeout(() => callback({ timeRemaining: () => 12 }), 80);
};

const preloadAllRecommendationImages = (carousel, startIndex = 0, batchSize = 4) => {
  const cards = Array.from(carousel.querySelectorAll('[data-dv-recommendation-card]'));
  let index = Math.max(0, startIndex);

  const runBatch = (deadline) => {
    let loaded = 0;

    while (index < cards.length && loaded < batchSize) {
      const hasTime = !deadline || deadline.timeRemaining?.() > 4 || loaded === 0;
      if (!hasTime) break;

      preloadRecommendationImage(cards[index]);
      index += 1;
      loaded += 1;
    }

    if (index < cards.length) {
      scheduleRecommendationIdleWork(runBatch);
    }
  };

  scheduleRecommendationIdleWork(runBatch);
};

const preloadUpcomingRecommendationImages = (carousel) => {
  const track = carousel.querySelector('[data-dv-carousel-track]');
  if (!track) return;

  const cards = Array.from(track.querySelectorAll('[data-dv-recommendation-card]'));
  const preloadEdge = track.scrollLeft + (track.clientWidth * 3.5);
  const firstUpcomingIndex = cards.findIndex((card) => card.offsetLeft + card.clientWidth >= preloadEdge);
  preloadRecommendationImages(carousel, Math.max(firstUpcomingIndex, 0), 16);
};

const loadCarouselImages = (carousel) => {
  const images = Array.from(carousel.querySelectorAll('.dv-card img, .dv-catalog-card img'));

  images.forEach((image, index) => {
    image.loading = 'eager';
    image.setAttribute('loading', 'eager');

    if (index >= 16) {
      if ('fetchPriority' in image) {
        image.fetchPriority = 'low';
      }
      image.setAttribute('fetchpriority', 'low');
    }

    if (image.hasAttribute('data-dv-carousel-image-requested')) return;
    image.setAttribute('data-dv-carousel-image-requested', 'true');

    const source = image.currentSrc || image.getAttribute('src');
    if (!source) return;

    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    if (image.sizes) preloadImage.sizes = image.sizes;
    if (image.srcset) preloadImage.srcset = image.srcset;
    preloadImage.src = source;
  });
};

const isLocalRecommendationStatsEndpoint = (endpoint) => {
  if (!endpoint) return false;

  try {
    const url = new URL(endpoint, window.location.origin);
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(url.hostname);
  } catch (_error) {
    return false;
  }
};

const reportRecommendationEvent = (carousel, card, eventName) => {
  const endpoint = carousel?.getAttribute('data-dv-stats-endpoint');
  const detail = {
    productId: recommendationProductId(card),
    productHandle: recommendationProductHandle(card),
    eventName,
  };

  window.dispatchEvent(new CustomEvent('dv:recommendation-stats', { detail }));

  if (!endpoint || isLocalRecommendationStatsEndpoint(endpoint) || !detail.productId) return;

  const body = JSON.stringify(detail);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    return;
  }

  fetch(endpoint, {
    method: 'POST',
    body,
    keepalive: true,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(() => {});
};

const recordRecommendationEvent = (card, eventName) => {
  const carousel = card?.closest?.('[data-dv-choice-recommendations]');
  const productId = recommendationProductId(card);
  if (!productId) return;

  const stats = readRecommendationStats();
  const productStats = stats[productId] || { views: 0, clicks: 0 };
  productStats[eventName] = (Number(productStats[eventName]) || 0) + 1;
  stats[productId] = productStats;
  writeRecommendationStats(stats);
  reportRecommendationEvent(carousel, card, eventName);
};

const carouselElements = (root = document) => {
  const elements = [];

  if (root?.matches?.('[data-dv-carousel]')) {
    elements.push(root);
  }

  root?.querySelectorAll?.('[data-dv-carousel]').forEach((carousel) => elements.push(carousel));
  return elements;
};

const removeUnavailableCarouselStock = (root = document) => {
  root.querySelectorAll?.('[data-dv-carousel] [data-dv-card-root]').forEach((card) => {
    const quickAdd = card.querySelector('.dv-catalog-card__quick-add');
    const selectedUnavailable = card.querySelector('[data-dv-card-variant-option][data-dv-card-variant-available="false"]:checked');

    if (quickAdd?.disabled || selectedUnavailable) {
      card.remove();
    }
  });

  root.querySelectorAll?.('[data-dv-carousel] [data-dv-card-variant-option][data-dv-card-variant-available="false"]').forEach((input) => {
    input.closest('label')?.remove();
  });

  root.querySelectorAll?.('[data-dv-carousel] .dv-card-size-picker').forEach((picker) => {
    if (!picker.querySelector('[data-dv-card-variant-option]')) {
      picker.closest('[data-dv-card-root]')?.remove();
    }
  });
};

const removeCurrentRecommendationProduct = (root = document) => {
  root.querySelectorAll?.('[data-dv-choice-recommendations][data-dv-current-product-id]').forEach((carousel) => {
    const currentProductId = carousel.getAttribute('data-dv-current-product-id');
    if (!currentProductId) return;

    carousel.querySelectorAll('[data-dv-card-root][data-dv-product-id]').forEach((card) => {
      if (card.getAttribute('data-dv-product-id') === currentProductId) {
        card.remove();
      }
    });
  });
};

const initDvCarousel = (root = document) => {
  carouselElements(root).forEach((carousel) => {
    if (carousel.hasAttribute('data-dv-carousel-ready')) return;

    const track = carousel.querySelector('[data-dv-carousel-track]');
    const buttons = Array.from(carousel.querySelectorAll('[data-dv-carousel-button]'));
    if (!track || buttons.length === 0) return;

    carousel.setAttribute('data-dv-carousel-ready', 'true');
    track.style.scrollBehavior = 'auto';

    let targetScroll = track.scrollLeft;
    let isProgrammaticScroll = false;
    let programmaticScrollTimer;
    let isLoadingMore = false;

    const maxCarouselScroll = () => Math.max(track.scrollWidth - track.clientWidth, 0);
    const clampCarouselScroll = (value) => Math.min(Math.max(value, 0), maxCarouselScroll());
    const nextPagination = () => track.querySelector('[data-dv-carousel-pagination][data-dv-carousel-next-url]');
    const hasNextPage = () => Boolean(nextPagination());

    const loadMoreCarouselItems = async () => {
      const pagination = nextPagination();
      const nextUrl = pagination?.getAttribute('data-dv-carousel-next-url');

      if (!nextUrl || isLoadingMore) return false;

      isLoadingMore = true;
      carousel.setAttribute('data-dv-carousel-loading', 'true');

      try {
        const response = await fetch(nextUrl, {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) return false;

        const template = document.createElement('template');
        template.innerHTML = (await response.text()).trim();

        pagination.remove();
        track.appendChild(template.content);
        removeUnavailableCarouselStock(carousel);
        removeCurrentRecommendationProduct(carousel);
        scheduleButtonUpdate();
        return true;
      } catch (_error) {
        return false;
      } finally {
        isLoadingMore = false;
        carousel.removeAttribute('data-dv-carousel-loading');
      }
    };

    const loadMoreIfNearEnd = () => {
      if (!hasNextPage()) return;

      const preloadEdge = track.scrollLeft + track.clientWidth;
      const threshold = track.scrollWidth - Math.max(track.clientWidth * 0.65, 240);

      if (preloadEdge >= threshold) {
        loadMoreCarouselItems();
      }
    };

    const updateButtons = () => {
      targetScroll = clampCarouselScroll(targetScroll);
      const maxScroll = maxCarouselScroll();
      const currentScroll = isProgrammaticScroll ? targetScroll : track.scrollLeft;

      buttons.forEach((button) => {
        const direction = button.getAttribute('data-dv-carousel-button');
        button.disabled = direction === 'prev'
          ? maxScroll <= 1 || currentScroll <= 1
          : (maxScroll <= 1 || currentScroll >= maxScroll - 1) && !hasNextPage();
      });
    };

    const scheduleButtonUpdate = () => {
      window.requestAnimationFrame(updateButtons);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', async () => {
        const direction = button.getAttribute('data-dv-carousel-button') === 'next' ? 1 : -1;
        const shouldLoadMore = direction === 1 && targetScroll >= maxCarouselScroll() - 4;

        if (shouldLoadMore) {
          await loadMoreCarouselItems();
        }

        targetScroll = clampCarouselScroll(targetScroll + (direction * Math.max(track.clientWidth * 0.82, 240)));
        isProgrammaticScroll = true;
        window.clearTimeout(programmaticScrollTimer);
        track.scrollLeft = targetScroll;
        programmaticScrollTimer = window.setTimeout(() => {
          isProgrammaticScroll = false;
          targetScroll = track.scrollLeft;
          updateButtons();
        }, 80);
        updateButtons();
      });
    });

    track.addEventListener('scroll', () => {
      if (!isProgrammaticScroll) targetScroll = track.scrollLeft;
      loadMoreIfNearEnd();
      scheduleButtonUpdate();
    }, { passive: true });
    window.addEventListener('resize', () => {
      targetScroll = clampCarouselScroll(track.scrollLeft);
      scheduleButtonUpdate();
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleButtonUpdate);
      resizeObserver.observe(track);
    }

    if (carousel.getAttribute('data-dv-carousel-preload') === 'all') {
      loadCarouselImages(carousel);
    }

    updateButtons();
  });
};

const DV_THEME_CSS_VERSION = '20260624faviconwrap';
const DV_INFO_PAGE_VERSION = '20260614headerrestore';
const DV_FONT_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap';

const dvInfoPageViews = {
  '/pages/customer-care': 'customer-care-pro',
  '/pages/delivery': 'delivery-pro',
  '/pages/returns': 'returns-pro',
  '/pages/faqs': 'faqs-pro',
  '/pages/contact': 'contact-pro',
  '/pages/our-story': 'our-story-pro',
};

const ensureCurrentThemeAssets = () => {
  const currentStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .find((link) => link.href.includes('/assets/dv-theme.css'));

  if (currentStylesheet && !currentStylesheet.href.includes(DV_THEME_CSS_VERSION)) {
    const nextStylesheet = document.createElement('link');
    nextStylesheet.rel = 'stylesheet';
    nextStylesheet.href = `${currentStylesheet.href.split('?')[0]}?v=${DV_THEME_CSS_VERSION}`;
    document.head.append(nextStylesheet);
  }

  const hasCurrentFontPackage = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .some((link) => (
      link.href.includes('fonts.googleapis.com')
      && link.href.includes('Instrument+Sans')
      && link.href.includes('Cormorant+Garamond')
    ));

  if (hasCurrentFontPackage) return;

  const fontStylesheet = document.createElement('link');
  fontStylesheet.rel = 'stylesheet';
  fontStylesheet.href = DV_FONT_STYLESHEET_URL;
  document.head.append(fontStylesheet);
};

const refreshStaleInfoPage = async () => {
  const path = window.location.pathname.replace(/\/$/, '');
  const view = dvInfoPageViews[path];
  const currentInfoPage = document.querySelector('.dv-info-page');

  if (!view || !currentInfoPage || currentInfoPage.getAttribute('data-dv-info-version') === DV_INFO_PAGE_VERSION) return;

  currentInfoPage.setAttribute('data-dv-info-refreshing', 'true');

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('view', view);
    url.searchParams.set('dv-info-refresh', String(Date.now()));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) return;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const freshInfoPage = doc.querySelector('.dv-info-page');

    if (!freshInfoPage || freshInfoPage.getAttribute('data-dv-info-version') !== DV_INFO_PAGE_VERSION) return;

    currentInfoPage.replaceWith(freshInfoPage);
  } catch (_error) {
    // Cached pages remain usable if the fresh view cannot be fetched.
  } finally {
    currentInfoPage.removeAttribute('data-dv-info-refreshing');
  }
};

const initInfoPageRecovery = () => {
  ensureCurrentThemeAssets();
  refreshStaleInfoPage();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfoPageRecovery, { once: true });
} else {
  initInfoPageRecovery();
}

removeUnavailableCarouselStock();
removeCurrentRecommendationProduct();
initDvCarousel();

document.addEventListener('shopify:section:load', (event) => {
  removeUnavailableCarouselStock(event.target);
  removeCurrentRecommendationProduct(event.target);
  initDvCarousel(event.target);
});

document.querySelectorAll('[data-dv-choice-recommendations]').forEach((carousel) => {
  const track = carousel.querySelector('[data-dv-carousel-track]');
  const viewed = new Set();

  sortRecommendationCards(carousel);
  const cards = Array.from(carousel.querySelectorAll('[data-dv-recommendation-card]'));
  preloadRecommendationImages(carousel, 0, 16);
  preloadAllRecommendationImages(carousel, 16, 4);

  const recordView = (card) => {
    const productId = recommendationProductId(card);
    if (!productId || viewed.has(productId)) return;
    viewed.add(productId);
    recordRecommendationEvent(card, 'views');
  };

  cards.forEach((card) => {
    card.querySelectorAll('[data-dv-recommendation-click]').forEach((link) => {
      link.addEventListener('click', () => recordRecommendationEvent(card, 'clicks'));
    });
  });

  if (track) {
    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => preloadUpcomingRecommendationImages(carousel));
    }, { passive: true });
  }

  carousel.querySelectorAll('[data-dv-carousel-button]').forEach((button) => {
    const preloadAhead = () => preloadUpcomingRecommendationImages(carousel);
    button.addEventListener('pointerenter', preloadAhead);
    button.addEventListener('focus', preloadAhead);
    button.addEventListener('touchstart', preloadAhead, { passive: true });
    button.addEventListener('click', () => {
      window.setTimeout(() => preloadUpcomingRecommendationImages(carousel), 120);
    });
  });

  const statsEndpoint = carousel.getAttribute('data-dv-stats-endpoint');
  if (statsEndpoint && !isLocalRecommendationStatsEndpoint(statsEndpoint)) {
    fetch(statsEndpoint, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((siteStats) => {
        if (!siteStats) return;
        window.dvRecommendationStats = siteStats;
        sortRecommendationCards(carousel, mergeRecommendationStats(readRecommendationStats(), siteStats));
        preloadRecommendationImages(carousel, 0, 16);
        preloadAllRecommendationImages(carousel, 16, 4);
      })
      .catch(() => {});
  }

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          recordView(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: track,
      threshold: 0.5,
    });

    cards.forEach((card) => observer.observe(card));
  } else {
    cards.forEach(recordView);
  }
});

const shopifyImageStem = (src = '') => {
  if (!src) return '';

  let pathname = String(src).split('?')[0];
  try {
    pathname = new URL(src, window.location.origin).pathname;
  } catch (_error) {
    // Best effort for protocol-relative or already-normalized image paths.
  }

  const filename = pathname.split('/').pop() || '';
  return filename
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '')
    .toLowerCase();
};

const shopifyImageStemsMatch = (left = '', right = '') => {
  const leftStem = shopifyImageStem(left);
  const rightStem = shopifyImageStem(right);
  if (!leftStem || !rightStem) return false;
  return leftStem === rightStem || leftStem.startsWith(`${rightStem}_`) || rightStem.startsWith(`${leftStem}_`);
};

document.querySelectorAll('[data-dv-gallery]').forEach((gallery) => {
  const slides = Array.from(gallery.querySelectorAll('[data-dv-gallery-slide]'));
  const thumbs = Array.from(gallery.querySelectorAll('[data-dv-gallery-thumb]'));
  const prev = gallery.querySelector('[data-dv-gallery-prev]');
  const next = gallery.querySelector('[data-dv-gallery-next]');
  const thumbRail = gallery.querySelector('[data-dv-gallery-thumbs]');
  const thumbPrev = gallery.querySelector('[data-dv-gallery-thumbs-prev]');
  const thumbNext = gallery.querySelector('[data-dv-gallery-thumbs-next]');
  let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

  const hydrateGallerySlide = (slide) => {
    const image = slide?.querySelector('img[data-dv-gallery-lazy-src]');
    if (!image) return;

    const lazySrc = image.getAttribute('data-dv-gallery-lazy-src');
    const lazySrcset = image.getAttribute('data-dv-gallery-lazy-srcset');

    if (lazySrcset) image.setAttribute('srcset', lazySrcset);
    if (lazySrc) image.setAttribute('src', lazySrc);

    image.removeAttribute('data-dv-gallery-lazy-src');
    image.removeAttribute('data-dv-gallery-lazy-srcset');
  };

  const updateThumbButtons = () => {
    if (!thumbRail || !thumbPrev || !thumbNext) return;
    const maxScroll = thumbRail.scrollWidth - thumbRail.clientWidth;
    const canScroll = maxScroll > 4;
    thumbPrev.hidden = !canScroll;
    thumbNext.hidden = !canScroll;
    if (!canScroll) return;
    thumbPrev.disabled = thumbRail.scrollLeft <= 4;
    thumbNext.disabled = thumbRail.scrollLeft >= maxScroll - 4;
  };

  const scrollThumbRail = (direction) => {
    if (!thumbRail) return;
    thumbRail.scrollBy({
      left: direction * Math.max(thumbRail.clientWidth * 0.7, 120),
      behavior: 'smooth',
    });
  };

  const scrollActiveThumbIntoRail = () => {
    const thumb = thumbs[active];
    if (!thumbRail || !thumb) return;

    const thumbLeft = thumb.offsetLeft;
    const thumbRight = thumbLeft + thumb.offsetWidth;
    const railLeft = thumbRail.scrollLeft;
    const railRight = railLeft + thumbRail.clientWidth;

    if (thumbLeft < railLeft) {
      thumbRail.scrollTo({ left: thumbLeft, behavior: 'smooth' });
    } else if (thumbRight > railRight) {
      thumbRail.scrollTo({ left: thumbRight - thumbRail.clientWidth, behavior: 'smooth' });
    }
  };

  const select = (index) => {
    if (!slides.length) return;
    active = (index + slides.length) % slides.length;
    hydrateGallerySlide(slides[active]);
    slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === active));
    thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === active));
    scrollActiveThumbIntoRail();
    updateThumbButtons();
  };

  const selectByMediaId = (mediaId) => {
    if (!mediaId) return false;
    const id = String(mediaId);
    const index = slides.findIndex((slide) => slide.getAttribute('data-dv-media-id') === id);
    if (index < 0) return false;
    select(index);
    return true;
  };

  const selectByImageSrc = (src) => {
    if (!src) return false;
    const index = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      const imageSrc = image?.getAttribute('data-dv-gallery-lazy-src') || image?.currentSrc || image?.src || '';
      return image && shopifyImageStemsMatch(imageSrc, src);
    });
    if (index < 0) return false;
    select(index);
    return true;
  };

  gallery.dvSelectMedia = selectByMediaId;
  gallery.dvSelectImage = selectByImageSrc;

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', () => select(index));
  });

  prev?.addEventListener('click', () => select(active - 1));
  next?.addEventListener('click', () => select(active + 1));
  thumbPrev?.addEventListener('click', () => scrollThumbRail(-1));
  thumbNext?.addEventListener('click', () => scrollThumbRail(1));
  thumbRail?.addEventListener('scroll', updateThumbButtons, { passive: true });
  window.addEventListener('resize', updateThumbButtons);
  updateThumbButtons();
});

const canonicalProductUrl = () => `${window.location.pathname.replace(/\/$/, '')}.js`;

const optionNameLooksVisual = (name = '') => /\b(colou?r|design|pattern|style|print)\b/i.test(name);
const optionNameLooksSize = (name = '') => /\b(size|specification)\b/i.test(name);

const optionGroupTypeFromName = (name = '') => {
  if (optionNameLooksVisual(name)) return 'visual';
  if (optionNameLooksSize(name)) return 'size';
  return 'button';
};

const customerOptionNameFromOption = (option = {}) => (
  optionNameLooksVisual(option.name || '') ? 'Colour / Design' : option.name || ''
);

const displayOptionValueName = (value = '', groupType = '') => {
  const stringValue = String(value || '');
  if (groupType !== 'size') return stringValue;
  return stringValue.replace(/ cover only/ig, '').trim();
};

const canonicalOptionValues = (product, variants, option, position) => {
  const values = Array.isArray(option?.values) ? option.values : [];
  const fromOptions = values
    .map((value) => (typeof value === 'object' ? value.name || value.value : value))
    .filter(Boolean);

  if (fromOptions.length) return fromOptions;

  return Array.from(new Set(
    variants
      .map((variant) => variant.options?.[position - 1] || variant[`option${position}`])
      .filter(Boolean),
  ));
};

const variantImageSrc = (variant) => (
  variant?.featured_media?.preview_image?.src
  || variant?.featured_image?.src
  || ''
);

const firstVariantForOptionValue = (variants, position, value, requireAvailable = false) => variants.find((variant) => (
  (!requireAvailable || variant.available)
  && (variant.options?.[position - 1] || variant[`option${position}`]) === value
  && variantImageSrc(variant)
));

const createCanonicalOptionLabel = ({
  option,
  position,
  value,
  customerOptionName,
  groupType,
  variants,
}) => {
  const label = document.createElement('label');
  label.className = `dv-option dv-option--${groupType}`;

  const input = document.createElement('input');
  input.type = 'radio';
  input.name = `option-${position}`;
  input.value = value;
  input.setAttribute('data-dv-option', '');
  input.setAttribute('data-dv-option-display-value', displayOptionValueName(value, groupType));
  input.setAttribute('data-position', String(position));
  input.setAttribute('data-option-name', customerOptionName);
  input.setAttribute('aria-label', `${option.name || customerOptionName} ${value}`);
  label.append(input);

  if (groupType === 'visual') {
    const variant = firstVariantForOptionValue(variants, position, value, true)
      || firstVariantForOptionValue(variants, position, value, false);
    const imageSrc = variantImageSrc(variant);
    if (!imageSrc) return null;

    const wrapper = document.createElement('span');
    wrapper.className = 'dv-image-option-label';
    wrapper.title = value;

    const image = document.createElement('img');
    image.src = imageSrc;
    image.alt = value;
    image.loading = 'lazy';
    image.width = 90;
    image.height = 90;
    wrapper.append(image);
    label.append(wrapper);
    return label;
  }

  const wrapper = document.createElement('span');
  wrapper.className = 'dv-size-label';
  const strong = document.createElement('strong');
  strong.textContent = displayOptionValueName(value, groupType);
  wrapper.append(strong);
  label.append(wrapper);
  return label;
};

const syncOptionControlsFromProduct = (form, product, variants) => {
  if (!form || !Array.isArray(product?.options) || !Array.isArray(variants) || !variants.length) return false;

  let changed = false;
  product.options.forEach((option, index) => {
    const position = Number(option.position || index + 1);
    const group = form.querySelector(`[data-dv-option][data-position="${position}"]`)?.closest('.dv-product__option-group')
      || form.querySelector(`[data-dv-option-label="${position}"]`)?.closest('.dv-product__option-group');
    const optionsContainer = group?.querySelector('.dv-options');
    if (!optionsContainer) return;

    const existingValues = new Set(
      Array.from(form.querySelectorAll(`[data-dv-option][data-position="${position}"]`))
        .map((input) => input.value),
    );
    const optionName = option.name || group.querySelector('[data-dv-option-label]')?.getAttribute('data-option-name') || '';
    const groupType = optionGroupTypeFromName(optionName);
    const customerOptionName = customerOptionNameFromOption(option);

    canonicalOptionValues(product, variants, option, position).forEach((value) => {
      if (!value || existingValues.has(value)) return;
      const label = createCanonicalOptionLabel({
        option,
        position,
        value,
        customerOptionName,
        groupType,
        variants,
      });
      if (!label) return;
      optionsContainer.append(label);
      existingValues.add(value);
      changed = true;
    });
  });

  return changed;
};

document.querySelectorAll('[data-dv-product-form]').forEach((form) => {
  const variantsScript = form.querySelector('[data-dv-product-variants]');
  const variantInput = form.querySelector('input[name="id"]');
  const priceNodes = document.querySelectorAll(`[data-dv-price="${form.dataset.productId}"]`);
  const addButton = form.querySelector('[data-dv-add]');
  const checkoutButton = form.querySelector('[data-dv-checkout-now]');
  const currency = form.getAttribute('data-currency') || 'GBP';
  const gallery = form.closest('.dv-product')?.querySelector('[data-dv-gallery]');
  const initialVariantId = variantInput?.value;
  let variants = [];

  try {
    variants = JSON.parse(variantsScript?.textContent || '[]');
  } catch (_error) {
    variants = [];
  }

  const refreshVariantsFromCanonicalProduct = async () => {
    try {
      const response = await fetch(canonicalProductUrl(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;

      const canonicalProduct = await response.json();
      if (!Array.isArray(canonicalProduct?.variants) || !canonicalProduct.variants.length) return false;

      const currentIds = new Set(variants.map((variant) => String(variant.id)));
      const hasCanonicalChanges = canonicalProduct.variants.length !== variants.length
        || canonicalProduct.variants.some((variant) => !currentIds.has(String(variant.id)));
      if (!hasCanonicalChanges) return false;

      variants = canonicalProduct.variants;
      if (variantsScript) {
        variantsScript.textContent = JSON.stringify(variants);
      }
      return syncOptionControlsFromProduct(form, canonicalProduct, variants);
    } catch (_error) {
      return false;
    }
  };

  const selectedOptions = () =>
    Array.from(form.querySelectorAll('[data-dv-option]:checked'))
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
      .map((input) => input.value);

  const optionPositions = () =>
    Array.from(new Set(
      Array.from(form.querySelectorAll('[data-dv-option]'))
        .map((input) => Number(input.dataset.position))
        .filter(Boolean),
    )).sort((a, b) => a - b);

  const selectedValuesByPosition = () => {
    const selected = {};
    form.querySelectorAll('[data-dv-option]:checked').forEach((input) => {
      selected[Number(input.dataset.position)] = {
        value: input.value,
        optionName: input.dataset.optionName || '',
      };
    });
    return selected;
  };

  const variantIdFromHash = () => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return '';
    const params = new URLSearchParams(hash);
    return params.get('variant') || '';
  };

  const selectVariantById = (variantId) => {
    if (!variantId) return false;
    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return false;

    variant.options.forEach((value, index) => {
      const position = index + 1;
      const optionInput = Array.from(form.querySelectorAll(`[data-dv-option][data-position="${position}"]`))
        .find((input) => input.value === value);
      if (optionInput) optionInput.checked = true;
    });

    if (variantInput) variantInput.value = variant.id;
    return true;
  };

  const isVisualOptionName = optionNameLooksVisual;
  const isSizeOptionName = optionNameLooksSize;

  const variantMatchesSelections = (variant, selections, ignoredPosition = 0, options = {}) =>
    Object.entries(selections).every(([position, selection]) => {
      const optionPosition = Number(position);
      if (optionPosition === ignoredPosition) return true;
      if (options.ignoreVisualSelections && isVisualOptionName(selection.optionName)) return true;
      return variant.options[optionPosition - 1] === selection.value;
    });

  const variantForSelections = (selections, options = {}) =>
    variants.find((item) => (
      (options.includeUnavailable || item.available)
      && item.options.every((option, index) => option === selections[index])
    ));

  const variantExistsForSelections = (selections, options = {}) =>
    Boolean(variantForSelections(selections, options));

  const optionHasCompatibleVariant = (input, selections) => {
    const position = Number(input.dataset.position);
    const ignoresVisualSelections = isSizeOptionName(input.dataset.optionName || '');
    return variants.some((variant) => (
      variant.available
      && variant.options[position - 1] === input.value
      && variantMatchesSelections(variant, selections, position, {
        ignoreVisualSelections: ignoresVisualSelections,
      })
    ));
  };

  const refreshOptionAvailability = (options = {}) => {
    const selections = selectedValuesByPosition();
    form.querySelectorAll('[data-dv-option]').forEach((input) => {
      const label = input.closest('.dv-option');
      const hasCompatibleVariant = optionHasCompatibleVariant(input, selections);
      const preserveSelectedInput = options.preserveSelectedVariant && input.checked;
      const shouldHide = !hasCompatibleVariant && !preserveSelectedInput;
      input.disabled = shouldHide;
      input.toggleAttribute('aria-disabled', !hasCompatibleVariant);
      if (label) {
        label.hidden = shouldHide;
        label.style.display = shouldHide ? 'none' : '';
        label.classList.toggle('is-unavailable', !hasCompatibleVariant);
      }
    });
  };

  const ensureValidSelections = (options = {}) => {
    if (
      options.preserveSelectedVariant
      && String(variantInput?.value || '') === String(initialVariantId || '')
      && variantExistsForSelections(selectedOptions(), { includeUnavailable: true })
    ) {
      return false;
    }

    let changed = false;
    for (const position of optionPositions()) {
      const checked = form.querySelector(`[data-dv-option][data-position="${position}"]:checked`);
      if (checked && !checked.disabled && !checked.closest('.dv-option')?.hidden) {
        continue;
      }
      const nextInput = form.querySelector(`[data-dv-option][data-position="${position}"]:not(:disabled)`);
      if (nextInput) {
        nextInput.checked = true;
        changed = true;
      }
    }
    return changed;
  };

  const reconcileOptionAvailability = (options = {}) => {
    for (let attempts = 0; attempts < 4; attempts += 1) {
      refreshOptionAvailability(options);
      if (!ensureValidSelections(options)) break;
    }
    refreshOptionAvailability(options);
  };

  const updateVariant = (options = {}) => {
    if (!variants.length || !variantInput) return;
    reconcileOptionAvailability(options);
    const selected = selectedOptions();
    const variant = variantForSelections(selected, { includeUnavailable: true });
    if (!variant) return;

    variantInput.value = variant.id;
    form.querySelectorAll('[data-dv-option-label]').forEach((label) => {
      const position = label.getAttribute('data-dv-option-label');
      const optionName = label.getAttribute('data-option-name') || `Option ${position}`;
      const isVisualOption = label.getAttribute('data-option-visual') === 'true';
      const selectedInput = form.querySelector(`[data-dv-option][data-position="${position}"]:checked`);
      if (selectedInput) {
        const selectedDisplayValue = selectedInput.dataset.dvOptionDisplayValue || selectedInput.value;
        const selectedValueNode = label.querySelector('[data-dv-option-label-value]');
        if (selectedValueNode) {
          selectedValueNode.textContent = selectedDisplayValue;
        } else {
          label.textContent = `${isVisualOption ? 'Colour / Design' : optionName}: ${selectedDisplayValue}`;
        }
      }
    });

    priceNodes.forEach((node) => {
      node.textContent = moneyFormat(variant.price, currency);
    });

    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.querySelector('[data-dv-add-text]').textContent = variant.available ? 'Add to Bag' : 'Sold Out';
    }
    if (checkoutButton) {
      checkoutButton.disabled = !variant.available;
    }

    const mediaId = variant.featured_media?.id || variant.featured_media_id;
    const selectedMedia = gallery?.dvSelectMedia?.(mediaId);
    if (!selectedMedia) {
      gallery?.dvSelectImage?.(variant.featured_image?.src);
    }

    if (options.updateUrl !== false && window.history?.replaceState && variant.id) {
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      url.hash = `variant=${variant.id}`;
      window.history.replaceState({}, '', url);
    }
  };

  const bindOptionListeners = () => {
    form.querySelectorAll('[data-dv-option]').forEach((input) => {
      if (input.hasAttribute('data-dv-option-listener-bound')) return;
      input.setAttribute('data-dv-option-listener-bound', 'true');
      input.addEventListener('change', updateVariant);
    });
  };

  bindOptionListeners();

  const hashVariantSelected = selectVariantById(variantIdFromHash());
  updateVariant({ updateUrl: false, preserveSelectedVariant: !hashVariantSelected });
  refreshVariantsFromCanonicalProduct().then((changed) => {
    if (!changed) return;
    bindOptionListeners();
    const refreshedHashVariantSelected = selectVariantById(variantIdFromHash());
    updateVariant({ updateUrl: false, preserveSelectedVariant: !refreshedHashVariantSelected });
  });

  form.querySelectorAll('[data-dv-qty]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = form.querySelector('input[name="quantity"]');
      if (!input) return;
      const direction = button.getAttribute('data-dv-qty') === 'plus' ? 1 : -1;
      const nextValue = Math.max(1, Number(input.value || 1) + direction);
      input.value = String(nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  checkoutButton?.addEventListener('click', async () => {
    if (checkoutButton.disabled) return;

    const previousText = checkoutButton.textContent;
    checkoutButton.disabled = true;
    checkoutButton.textContent = 'Opening Checkout...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error('Unable to start checkout');
      window.location.href = '/checkout';
    } catch (_error) {
      checkoutButton.disabled = false;
      checkoutButton.textContent = previousText || 'Checkout Now';
    }
  });
});

const updateCardVariantPrice = (form, selectedOption) => {
  const card = form.closest('[data-dv-card-root]');
  const priceContainer = card?.querySelector('[data-dv-card-price]');
  const variantPrice = selectedOption.dataset.dvCardVariantPrice;

  if (!priceContainer || !variantPrice) return;

  const isSale = selectedOption.dataset.dvCardVariantOnSale === 'true';
  const compareAtPrice = selectedOption.dataset.dvCardVariantCompareAt;
  const saving = selectedOption.dataset.dvCardVariantSaving;
  const savingClass = priceContainer.classList.contains('dv-card__price')
    ? 'dv-card__saving'
    : 'dv-catalog-card__saving';

  priceContainer.replaceChildren();

  const price = document.createElement('span');
  price.className = isSale ? 'price price--sale' : 'price';
  price.textContent = variantPrice;
  priceContainer.append(price);

  if (isSale && compareAtPrice) {
    const compareAt = document.createElement('s');
    compareAt.className = 'compare-at-price';
    compareAt.textContent = compareAtPrice;
    priceContainer.append(compareAt);
  }

  if (isSale && saving) {
    const savingText = document.createElement('span');
    savingText.className = savingClass;
    savingText.textContent = `Save ${saving}`;
    priceContainer.append(savingText);
  }
};

document.addEventListener('change', (event) => {
  const selectedOption = event.target.closest?.('[data-dv-card-variant-option]');
  if (!selectedOption) return;

  const form = selectedOption.closest('.dv-catalog-card__form');
  if (!form) return;

  const selectedVariantId = selectedOption.dataset.dvCardVariantId || selectedOption.value;
  const variantInput = form.querySelector('input[name="id"]');
  const button = form.querySelector('.dv-catalog-card__quick-add');
  const available = selectedOption.dataset.dvCardVariantAvailable !== 'false' && !selectedOption.disabled;

  if (variantInput && selectedVariantId) {
    variantInput.value = selectedVariantId;
  }

  if (button) {
    button.disabled = !available;
    button.textContent = available ? 'Add to Bag' : 'Sold Out';
  }

  const card = form.closest('[data-dv-card-root]');
  if (card && selectedVariantId) {
    card.setAttribute('data-dv-variant-id', selectedVariantId);
  }

  updateCardVariantPrice(form, selectedOption);
});

document.addEventListener('click', (event) => {
  const quantityButton = event.target.closest?.('[data-dv-card-qty]');
  if (!quantityButton) return;

  const form = quantityButton.closest('.dv-catalog-card__form');
  const quantityInput = form?.querySelector('.dv-catalog-card__quantity-input');
  if (!quantityInput) return;

  const direction = quantityButton.getAttribute('data-dv-card-qty') === 'plus' ? 1 : -1;
  const min = Number(quantityInput.getAttribute('min') || 1);
  const current = Number(quantityInput.value || min);
  const nextValue = Math.max(min, current + direction);
  quantityInput.value = String(nextValue);
  quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('change', (event) => {
  const quantityInput = event.target.closest?.('.dv-catalog-card__quantity-input');
  if (!quantityInput) return;

  const min = Number(quantityInput.getAttribute('min') || 1);
  const value = Number(quantityInput.value || min);
  quantityInput.value = String(Math.max(min, Math.floor(value || min)));
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('.dv-catalog-card__form');
  if (!form) return;

  event.preventDefault();

  const button = form.querySelector('.dv-catalog-card__quick-add');
  if (!button || button.disabled) return;

  const previousText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding...';

  try {
    const quantityInput = form.querySelector('.dv-catalog-card__quantity-input');
    if (quantityInput) {
      const min = Number(quantityInput.getAttribute('min') || 1);
      const value = Number(quantityInput.value || min);
      quantityInput.value = String(Math.max(min, Math.floor(value || min)));
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Unable to add item');

    button.textContent = 'Added';

    try {
      const cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        document.querySelectorAll('.dv-cart-count').forEach((count) => {
          count.textContent = String(cart.item_count || 0);
        });
      }
    } catch (_error) {
      // Cart-count refresh is best-effort; the item has already been added.
    }

    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = previousText || 'Add to Bag';
    }, 2200);
  } catch (_error) {
    button.textContent = 'Try Again';
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = previousText || 'Add to Bag';
    }, 2200);
  }
});

const LOCAL_CATALOG_FILTER_TYPES = ['tier', 'product-type', 'material', 'size'];

const createLocalCatalogFilterMap = () => ({
  tier: new Set(),
  'product-type': new Set(),
  material: new Set(),
  size: new Set(),
});

const catalogItems = () => [...document.querySelectorAll('.product-grid__item[data-dv-local-filter-card]')];

const itemLocalFilterValues = (item, type) => {
  const rawValue = item.getAttribute(`data-dv-${type}`) || '';

  return rawValue
    .split(/\s+/)
    .map((value) => value.trim())
    .filter(Boolean);
};

const refreshLocalCatalogFilterOptions = () => {
  const availableByType = createLocalCatalogFilterMap();

  catalogItems().forEach((item) => {
    LOCAL_CATALOG_FILTER_TYPES.forEach((type) => {
      itemLocalFilterValues(item, type).forEach((value) => {
        availableByType[type].add(value);
      });
    });
  });

  document.querySelectorAll('[data-dv-local-filter-option]').forEach((option) => {
    const type = option.getAttribute('data-dv-local-filter-option');
    const value = option.getAttribute('data-dv-local-filter-value') || '';
    const isAvailable = Boolean(availableByType[type]?.has(value));
    option.hidden = !isAvailable;

    const input = option.querySelector('.dv-local-filter__input');
    if (input) {
      input.disabled = !isAvailable;
      if (!isAvailable) input.checked = false;
    }
  });

  document.querySelectorAll('[data-dv-local-filter-panel]').forEach((panel) => {
    const type = panel.getAttribute('data-dv-local-filter-panel');
    panel.hidden = !availableByType[type]?.size;
  });
};

const applyLocalCatalogFilters = () => {
  const selectedValues = createLocalCatalogFilterMap();

  document.querySelectorAll('.dv-local-filter__input:checked').forEach((input) => {
    const type = input.getAttribute('data-dv-local-filter');
    if (selectedValues[type]) selectedValues[type].add(input.value);
  });

  catalogItems().forEach((item) => {
    const matchesAllFilters = LOCAL_CATALOG_FILTER_TYPES.every((type) => {
      const selected = type === 'size' ? selectedValues.size : selectedValues[type];
      if (!selected.size) return true;

      return itemLocalFilterValues(item, type).some((value) => selected.has(value));
    });

    item.hidden = !matchesAllFilters;
  });
};

let priceSliderDragState = null;

const priceSliderElements = (wrapper) => ({
  minSlider: wrapper?.querySelector?.('[data-dv-price-slider-min]'),
  maxSlider: wrapper?.querySelector?.('[data-dv-price-slider-max]'),
  minHandle: wrapper?.querySelector?.('[data-dv-price-slider-handle="min"]'),
  maxHandle: wrapper?.querySelector?.('[data-dv-price-slider-handle="max"]'),
  track: wrapper?.querySelector?.('[data-dv-price-slider-track]'),
  priceFacet: wrapper?.closest?.('price-facet-component'),
});

const clampPriceSliderValue = (value, min, max) => Math.min(Math.max(value, min), max);

const priceSliderNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const priceSliderRangeMax = (minSlider, maxSlider) => {
  const max = priceSliderNumber(maxSlider?.max, priceSliderNumber(minSlider?.max, 0));
  return max > 0 ? max : 0;
};

const formatPriceSliderValue = (value) => {
  const rounded = Math.round(value * 100) / 100;
  return String(Number(rounded.toFixed(2)));
};

const priceSliderStep = (slider) => {
  const step = priceSliderNumber(slider?.step, 1);
  return step > 0 ? step : 1;
};

const valueFromPriceSliderPointer = (wrapper, slider, clientX) => {
  const { track } = priceSliderElements(wrapper);
  const rect = (track || wrapper)?.getBoundingClientRect?.();
  if (!rect?.width) return priceSliderNumber(slider?.value, 0);

  const min = priceSliderNumber(slider?.min, 0);
  const max = priceSliderNumber(slider?.max, min);
  const ratio = clampPriceSliderValue((clientX - rect.left) / rect.width, 0, 1);
  if (ratio <= 0) return min;
  if (ratio >= 1) return max;

  const step = priceSliderStep(slider);
  const rawValue = min + ((max - min) * ratio);
  return clampPriceSliderValue(Math.round(rawValue / step) * step, min, max);
};

const nearestPriceSliderHandle = (wrapper, clientX) => {
  const { minHandle, maxHandle } = priceSliderElements(wrapper);
  const minRect = minHandle?.getBoundingClientRect?.();
  const maxRect = maxHandle?.getBoundingClientRect?.();
  if (!minRect || !maxRect) return 'min';

  const minCenter = minRect.left + (minRect.width / 2);
  const maxCenter = maxRect.left + (maxRect.width / 2);
  return Math.abs(clientX - minCenter) <= Math.abs(clientX - maxCenter) ? 'min' : 'max';
};

const syncPriceSliderValues = (wrapper, activeHandle = 'min') => {
  const { minSlider, maxSlider, minHandle, maxHandle, priceFacet } = priceSliderElements(wrapper);
  const minInput = priceFacet?.querySelector('[ref="minInput"]');
  const maxInput = priceFacet?.querySelector('[ref="maxInput"]');
  if (!wrapper || !minSlider || !maxSlider || !minInput || !maxInput) return;

  const max = priceSliderRangeMax(minSlider, maxSlider);
  let minValue = priceSliderNumber(minSlider.value, 0);
  let maxValue = priceSliderNumber(maxSlider.value, max);

  if (minValue > maxValue) {
    if (activeHandle === 'min') {
      maxValue = minValue;
    } else {
      minValue = maxValue;
    }
  }

  minSlider.value = formatPriceSliderValue(minValue);
  maxSlider.value = formatPriceSliderValue(maxValue);
  minInput.value = minValue > 0 ? formatPriceSliderValue(minValue) : '';
  maxInput.value = maxValue < max ? formatPriceSliderValue(maxValue) : '';

  minHandle?.setAttribute('aria-valuenow', formatPriceSliderValue(minValue));
  maxHandle?.setAttribute('aria-valuenow', formatPriceSliderValue(maxValue));

  updatePriceSliderVisualState(wrapper);
};

function updatePriceSliderVisualState(wrapper) {
  const { minSlider, maxSlider } = priceSliderElements(wrapper);
  if (!wrapper || !minSlider || !maxSlider) return;

  const max = priceSliderRangeMax(minSlider, maxSlider);
  const minValue = priceSliderNumber(minSlider.value, 0);
  const maxValue = priceSliderNumber(maxSlider.value, max);
  const minPosition = max > 0 ? Math.min(100, Math.max(0, (minValue / max) * 100)) : 0;
  const maxPosition = max > 0 ? Math.min(100, Math.max(0, (maxValue / max) * 100)) : 100;

  wrapper.style.setProperty('--dv-price-min-position', `${minPosition}%`);
  wrapper.style.setProperty('--dv-price-max-position', `${maxPosition}%`);
}

function setPriceSliderValueFromPointer(wrapper, handleType, clientX) {
  const { minSlider, maxSlider } = priceSliderElements(wrapper);
  if (!wrapper || !minSlider || !maxSlider) return;

  const max = priceSliderRangeMax(minSlider, maxSlider);
  const currentMin = priceSliderNumber(minSlider.value, 0);
  const currentMax = priceSliderNumber(maxSlider.value, max);

  if (handleType === 'max') {
    const nextMax = valueFromPriceSliderPointer(wrapper, maxSlider, clientX);
    maxSlider.value = formatPriceSliderValue(Math.max(nextMax, currentMin));
  } else {
    const nextMin = valueFromPriceSliderPointer(wrapper, minSlider, clientX);
    minSlider.value = formatPriceSliderValue(Math.min(nextMin, currentMax));
  }

  syncPriceSliderValues(wrapper, handleType);
}

const commitPriceSliderHandle = (wrapper, handleType) => {
  const { priceFacet } = priceSliderElements(wrapper);
  const input = handleType === 'max'
    ? priceFacet?.querySelector('[ref="maxInput"]')
    : priceFacet?.querySelector('[ref="minInput"]');

  input?.dispatchEvent(new Event('change', { bubbles: true }));
};

function beginPriceSliderDrag(event) {
  const target = event.target.closest?.('[data-dv-price-slider-handle], [data-dv-price-slider-track], [data-dv-price-slider-fill]');
  if (!target || event.button > 0) return;

  const wrapper = target.closest('[data-dv-price-slider]');
  if (!wrapper) return;

  const handleType = target.getAttribute('data-dv-price-slider-handle') || nearestPriceSliderHandle(wrapper, event.clientX);
  priceSliderDragState = {
    wrapper,
    handleType,
    pointerId: event.pointerId,
  };

  event.preventDefault();
  wrapper.setAttribute('data-dv-price-dragging', handleType);
  wrapper.setPointerCapture?.(event.pointerId);
  setPriceSliderValueFromPointer(wrapper, handleType, event.clientX);
}

const updatePriceSliderDrag = (event) => {
  if (!priceSliderDragState || priceSliderDragState.pointerId !== event.pointerId) return;

  event.preventDefault();
  setPriceSliderValueFromPointer(priceSliderDragState.wrapper, priceSliderDragState.handleType, event.clientX);
};

const endPriceSliderDrag = (event) => {
  if (!priceSliderDragState || priceSliderDragState.pointerId !== event.pointerId) return;

  const { wrapper, handleType, pointerId } = priceSliderDragState;
  wrapper.removeAttribute('data-dv-price-dragging');
  wrapper.releasePointerCapture?.(pointerId);
  priceSliderDragState = null;
  commitPriceSliderHandle(wrapper, handleType);
};

const stepPriceSliderHandle = (event) => {
  const handle = event.target.closest?.('[data-dv-price-slider-handle]');
  if (!handle) return;

  const wrapper = handle.closest('[data-dv-price-slider]');
  const { minSlider, maxSlider } = priceSliderElements(wrapper);
  if (!wrapper || !minSlider || !maxSlider) return;

  const handleType = handle.getAttribute('data-dv-price-slider-handle') === 'max' ? 'max' : 'min';
  const activeSlider = handleType === 'max' ? maxSlider : minSlider;
  const step = priceSliderStep(activeSlider) * (event.shiftKey ? 10 : 1);
  const min = priceSliderNumber(activeSlider.min, 0);
  const max = priceSliderRangeMax(minSlider, maxSlider);
  const current = priceSliderNumber(activeSlider.value, handleType === 'max' ? max : min);
  let nextValue = current;

  if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') nextValue = current - step;
  else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') nextValue = current + step;
  else if (event.key === 'Home') nextValue = min;
  else if (event.key === 'End') nextValue = max;
  else return;

  event.preventDefault();
  if (handleType === 'max') {
    maxSlider.value = formatPriceSliderValue(Math.max(clampPriceSliderValue(nextValue, min, max), priceSliderNumber(minSlider.value, min)));
  } else {
    minSlider.value = formatPriceSliderValue(Math.min(clampPriceSliderValue(nextValue, min, max), priceSliderNumber(maxSlider.value, max)));
  }
  syncPriceSliderValues(wrapper, handleType);
  commitPriceSliderHandle(wrapper, handleType);
};

const refreshPriceSliders = (root = document) => {
  root.querySelectorAll?.('[data-dv-price-slider]').forEach((wrapper) => {
    updatePriceSliderVisualState(wrapper);
  });
};

const closeDvMobileNav = (header) => {
  const targetHeader = header || document.querySelector('.dv-header.is-mobile-nav-open');
  if (!targetHeader) return;

  const toggle = targetHeader.querySelector('[data-dv-mobile-menu-toggle]');
  const nav = targetHeader.querySelector('[data-dv-mobile-nav]');
  targetHeader.classList.remove('is-mobile-nav-open');
  toggle?.setAttribute('aria-expanded', 'false');
  toggle?.setAttribute('aria-label', 'Open menu');
  nav?.setAttribute('aria-hidden', 'true');
};

const toggleDvMobileNav = (toggle) => {
  const header = toggle.closest('.dv-header');
  if (!header) return;

  const nav = header.querySelector('[data-dv-mobile-nav]');
  const isOpen = !header.classList.contains('is-mobile-nav-open');
  header.classList.toggle('is-mobile-nav-open', isOpen);
  toggle.setAttribute('aria-expanded', String(isOpen));
  toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
  nav?.setAttribute('aria-hidden', String(!isOpen));
};

document.addEventListener('DOMContentLoaded', () => {
  refreshPriceSliders();
  refreshLocalCatalogFilterOptions();
  applyLocalCatalogFilters();

  const collectionRoot = document.querySelector('results-list') || document.body;
  let refreshTimer;
  const observer = new MutationObserver(() => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refreshPriceSliders();
      refreshLocalCatalogFilterOptions();
      applyLocalCatalogFilters();
    }, 50);
  });
  observer.observe(collectionRoot, { childList: true, subtree: true });
});

document.addEventListener('change', (event) => {
  if (event.target.closest?.('.dv-local-filter__input')) {
    applyLocalCatalogFilters();
  }
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest?.('[data-dv-mobile-menu-toggle]');
  if (toggle) {
    event.preventDefault();
    toggleDvMobileNav(toggle);
    return;
  }

  const mobileNavLink = event.target.closest?.('.dv-mobile-nav__link');
  if (mobileNavLink) {
    closeDvMobileNav(mobileNavLink.closest('.dv-header'));
    return;
  }

  const openHeader = document.querySelector('.dv-header.is-mobile-nav-open');
  if (openHeader && !event.target.closest?.('.dv-header')) {
    closeDvMobileNav(openHeader);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeDvMobileNav();
  }
});

document.addEventListener('pointerdown', beginPriceSliderDrag);
document.addEventListener('pointermove', updatePriceSliderDrag);
document.addEventListener('pointerup', endPriceSliderDrag);
document.addEventListener('pointercancel', endPriceSliderDrag);
document.addEventListener('keydown', stepPriceSliderHandle);

document.addEventListener('input', (event) => {
  const slider = event.target.closest?.('.dv-price-slider__range');
  if (!slider) return;

  const wrapper = slider.closest('[data-dv-price-slider]');
  const minSlider = wrapper?.querySelector('[data-dv-price-slider-min]');
  const maxSlider = wrapper?.querySelector('[data-dv-price-slider-max]');
  if (!minSlider || !maxSlider) return;

  let minValue = Number(minSlider.value || 0);
  let maxValue = Number(maxSlider.value || 0);

  if (minValue > maxValue) {
    if (slider === minSlider) {
      maxValue = minValue;
      maxSlider.value = String(maxValue);
    } else {
      minValue = maxValue;
      minSlider.value = String(minValue);
    }
  }

  minSlider.value = formatPriceSliderValue(minValue);
  maxSlider.value = formatPriceSliderValue(maxValue);
  syncPriceSliderValues(wrapper, slider.matches('[data-dv-price-slider-max]') ? 'max' : 'min');
});

document.addEventListener('change', (event) => {
  const slider = event.target.closest?.('.dv-price-slider__range');
  if (!slider) return;

  const priceFacet = slider.closest('price-facet-component');
  const input = slider.matches('[data-dv-price-slider-min]')
    ? priceFacet?.querySelector('[ref="minInput"]')
    : priceFacet?.querySelector('[ref="maxInput"]');
  input?.dispatchEvent(new Event('change', { bubbles: true }));
});
